import { semanticColors, type ColorToken, type ThemeName } from './colors.ts'
import { cssVariableName } from './css.ts'
import { componentRadii, componentSpacing, fontSize, motion, radii, spacing } from './scales.ts'

/**
 * Every token as a CSS variable, grouped the way a designer thinks about
 * them: what the staging design editor lists, tweaks live and exports.
 * Colours are per theme; everything else is the same in every theme.
 */
export type TokenKind = 'color' | 'length' | 'time' | 'easing'

export interface TokenEntry {
  /** The CSS variable: `--color-surface-base`. */
  readonly name: string
  readonly kind: TokenKind
  /** Its value in the source (packages/ui-tokens). */
  readonly value: string
}

export interface TokenGroup {
  readonly id: string
  readonly label: string
  /** Colours: a change belongs to the theme it was made in. */
  readonly themed: boolean
  readonly tokens: readonly TokenEntry[]
}

const COLOR_GROUPS: { id: string; label: string; match: (token: string) => boolean }[] = [
  { id: 'surfaces', label: 'Surfaces', match: (t) => t.startsWith('surface-') },
  { id: 'text', label: 'Text', match: (t) => t.startsWith('content-') },
  // What is typed into a field (`text-input`): tuned apart from the page's text.
  { id: 'inputs', label: 'Inputs', match: (t) => t.startsWith('input-') },
  { id: 'borders', label: 'Borders', match: (t) => t.startsWith('border-') },
  { id: 'brand', label: 'Brand (buttons, links)', match: (t) => t.startsWith('brand-') },
  { id: 'accent', label: 'Accent and tags', match: (t) => /^(accent|violet|pink|teal)-/.test(t) },
  { id: 'swatch', label: 'Arrangement colours', match: (t) => /^swatch-/.test(t) },
  { id: 'status', label: 'Status', match: (t) => /^(success|warning|danger|info)-/.test(t) },
]

const entries = (prefix: string, scale: Record<string, string>, kind: TokenKind): TokenEntry[] =>
  Object.entries(scale).map(([key, value]) => ({ name: `--${prefix}-${key}`, kind, value }))

export function tokenCatalog(theme: ThemeName): TokenGroup[] {
  const colors = Object.entries(semanticColors[theme]) as [ColorToken, string][]
  return [
    ...COLOR_GROUPS.map((group) => ({
      id: group.id,
      label: group.label,
      themed: true,
      tokens: colors
        .filter(([token]) => group.match(token))
        .map(([token, value]) => ({ name: cssVariableName(token), kind: 'color' as const, value })),
    })),
    {
      id: 'components',
      label: 'Component spacing',
      themed: false,
      tokens: entries('spacing', componentSpacing, 'length'),
    },
    {
      id: 'component-radii',
      label: 'Component corners',
      themed: false,
      tokens: entries('radius', componentRadii, 'length'),
    },
    {
      id: 'motion',
      label: 'Motion',
      themed: false,
      tokens: [
        ...entries('duration', motion.duration, 'time'),
        ...entries('ease', motion.ease, 'easing'),
      ],
    },
    {
      id: 'spacing',
      label: 'Spacing scale',
      themed: false,
      tokens: [
        // Tailwind's base unit: every step not named below (h-14, top-25) is a multiple of it.
        { name: '--spacing', kind: 'length', value: '0.25rem' },
        ...entries('spacing', spacing, 'length').filter((t) => t.name !== '--spacing-px'),
      ],
    },
    {
      id: 'radii',
      label: 'Corner scale',
      themed: false,
      tokens: entries('radius', radii, 'length'),
    },
    { id: 'type', label: 'Type sizes', themed: false, tokens: entries('text', fontSize, 'length') },
  ]
}
